import React, { useEffect, useReducer, useState } from "react";
import PageHeader from "../../general/PageHeader";
import dayjs from "dayjs";
import useFilters from "../../../hooks/useFilters";
import { formatCurrency, getLink, getVendedoresForDropdown, getZonasForDropdown, permisions, toast, validarPINSeguridad } from "../../../utils/helpers";
import Select from "react-select";
import FiltersContainer from "../../general/FiltersContainer";
import { postRequest, request } from "../../../utils/request";
import { Link } from "react-router-dom";
import Pagination from "../../general/Pagination";
import Loader from "../../general/Loader";
import Modal from "../../general/Modal";
import GenerarReporteDespachoForm from "./GenerarReporteDespachoForm";
import Dropdown from "../../general/Dropdown";
import { auth } from "../../../utils/auth";
import AvatarEmpleado from "../../general/AvatarEmpleado";
import DespachoSummary from "./DespachoSummary";
import { transform } from "lodash";

const selectedReducer = (state, {type, payload}) => {
	switch (type) {
		case "add":
			if (state.tipo_despacho && state.tipo_despacho !== payload.factura.tipo_despacho) {
                return state;
			}

            if (state.facturas.some((factura) => factura.id === payload.factura.id)) {
                return state;
            }

			return {
				...state,
                tipo_despacho: payload.factura.tipo_despacho,
				facturas: [...state.facturas, payload.factura],
			};
        case "all":
            if (!state.tipo_despacho) return state;

            return {
                ...state,
                facturas: [...state.facturas, ...payload.facturas.filter((factura) => factura.tipo_despacho === state.tipo_despacho)],
                allSelected: true,
            }
        
		case "remove":
			const facturas = state.facturas.filter((factura) => factura.id !== payload.factura.id);
			return {
				...state,
				tipo_despacho: facturas.length > 0 ? facturas[0].tipo_despacho : null,
				facturas,
                allSelected: false,
			};

        case "reset":
        default:
			return {
				tipo_despacho: null,
				facturas: [],
                allSelected: false,
			};
	}
};

const DespachoFacturas = () => {
    const [filters, setFilters] = useState({
        desde: "",
        hasta: dayjs().endOf("day").format("YYYY-MM-DD"),
    });
    const { handleFilterChange, filtersQuery, setFilters: setUseFilters } = useFilters(filters);
    const [showDespacho, setShowDespacho] = useState(true);
    const [zonas, setZonas] = useState([]);
    const [vendedores, setVendedores] = useState([]);
    const [empleados, setEmpleados] = useState([]);
    const [vehiculos, setVehiculos] = useState([]);
    const [seguimientos, setSeguimientos] = useState([]);
    const [tipos, setTipos] = useState([]);
    const estados = [
        {value: '', label: 'Todos'},
        {value: 'abierto', label: 'Abierto'},
        {value: 'cerrado', label: 'Cerrado'},
    ];
    const [facturas, setFacturas] = useState({ data: [] });
    const [despachos, setDespachos] = useState({ data: [] });
    const [isLoading, setIsLoading] = useState(false);
    const [selected, setSelected] = useReducer(selectedReducer, {
        tipo_despacho: null,
        facturas: [],
        allSelected: false,
    });
    const [showGenerarDespachoReporteModal, setShowGenerarDespachoReporteModal] = useState(false);
    const [showDespacharTodasModal, setShowDespacharTodasModal] = useState(false);
    const [despachandoTodas, setDespachandoTodas] = useState(false);
    const [pinSeguridad, setPinSeguridad] = useState("");
    const [showReprogramarModal, setShowReprogramarModal] = useState(false);
    const [facturaReprogramar, setFacturaReprogramar] = useState(null);
    const [reprogramandoVisita, setReprogramandoVisita] = useState(false);
    const [summary, setSummary] = useState({});
    const [isSummaring, setIsSummaring] = useState(false);

    const [despacho, setDespacho] = useState(null);
    const [showCloseModal, setShowCloseModal] = useState(false);
    const [showReopenModal, setShowReopenModal] = useState(false);
    const [ejecutando, setEjecutando] = useState(false);

    const openCloseModal = (item) => {
        setDespacho(item);

        setShowCloseModal(true);
    };

    const openReOpenModal = (item) => {
        setDespacho(item);

        setPinSeguridad("");

        setShowReopenModal(true);
    };

    const closeCloseModal = () => {
        setDespacho(null);

        setShowCloseModal(false);
    };
    
    const closeReOpenModal = () => {
        setDespacho(null);

        setPinSeguridad("");

        setShowReopenModal(false);
    };

    const closeDespacho = async () => {
        if (ejecutando) return;
        
        setEjecutando(true);

        const response = await postRequest(`despachos/${despacho.id}/cerrar`);

        if(response.success) {
            getFacturas(`despachos${filtersQuery}`);
            toast(`Se cerro el despacho No: '${despachos.numero_despacho}'`);
            closeCloseModal();
        } else {
            toast(response.data ?? "Error cerrando el despacho.", 'error');
        }

        setEjecutando(false);
    };

    const reabrirDespacho = async () => {
        if (!pinSeguridad) {
            toast("Pin de seguridad requerido", 'error');
            return;
        }

        if (ejecutando) return;

        setEjecutando(true);
        
        const form = new FormData();
        form.append('pin_seguridad', pinSeguridad);
        const response = await postRequest(`despachos/${despacho.id}/reabrir`, form);

        if(response.success) {
            getFacturas(`despachos${filtersQuery}`);

            toast(`El despacho: '${despacho.numero_despacho}' reabierto`);
            closeReOpenModal();
        } else {
            toast(response?.data?.message ? response.data.message : "Error cerrando el despacho.", 'error');
        }

        setEjecutando(false);
    };

    const openReprogramar = (factura) => {
        setPinSeguridad("");
        setFacturaReprogramar(factura);
        setShowReprogramarModal(true);
    };

    const closeReprogramar = () => {
        setPinSeguridad("");
        setFacturaReprogramar(null);
        setShowReprogramarModal(false);
    };

    const getOptions = async () => {
        setSeguimientos([
            { value: "", label: "Todos" },
            { value: "por_cargar", label: "Por Cargar" },
            { value: "por_entregar", label: "Por Entregar" },
        ]);

        setTipos([
            { value: "", label: "Todas" },
            { value: "local", label: "Local" },
            { value: "domicilio", label: "A domicilio" },
        ]);

        const [zonaData, vendedorData, empleadosResponse, vehiculosResponse] = await Promise.all([
            getZonasForDropdown(),
            getVendedoresForDropdown(),
            request("empleados?limit=100"),
            request("vehiculos?limit=100"),
        ]);

        const [todas, ...zonas] = zonaData;
        setZonas([todas, { value: '0', label: "Local" }, ...zonas]);
        const [todos, ...vendedores] = vendedorData;
        setVendedores([todos, { value: '0', label: "Sin Vendedor" }, ...vendedores]);

        const empleadosData = empleadosResponse.data.map((empleado) => ({
            value: empleado.id,
            label: `${empleado.codigo} ${empleado.nombre} ${empleado.apellido}`,
            active: empleado.active,
        }));
        setEmpleados([{value: "", label: "Todos"}, ...empleadosData]);

        const vehiculosData = vehiculosResponse.data.map((vehiculo) => ({
            value: vehiculo.id,
            label: vehiculo.ficha,
        }));
        setVehiculos([{value: "", label: "Todos"}, ...vehiculosData]);
    };

    const getFacturas = async (url) => {
        setIsLoading(true);

        const base = showDespacho ? 'facturas-despacho' : 'despachos';
        url = url ? url + "&limit=15" : `${base}?limit=15`;
        const data = await request(url);

        if(showDespacho) {
            setFacturas(data);

            if(selected.allSelected) {
                setSelected({ type: "all", payload: { facturas: data.data } });
            }
        }
        else setDespachos(data);

        setIsLoading(false);
    };

    const getSummary = async (url) => {
        if (!showDespacho) return;

        setIsSummaring(true);

        url = url ? url + "&limit=15" : 'facturas-despacho-summary?limit=15';
        const response = await request(url);

        setSummary(response.data);

        setIsSummaring(false);
    };

    const handleSelect = (factura) => {
        const isSelected = selected.facturas.some((f) => f.id === factura.id);
        if (isSelected) {
            setSelected({ type: "remove", payload: { factura } });
        } else {
            setSelected({ type: "add", payload: { factura } });
        }
    };

    const onFilterChange = (field, value) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
        handleFilterChange(field, value);
    };

    const openDespacharTodasModal = () => {
        setPinSeguridad("");
        setShowDespacharTodasModal(true);
    };

    const closeDespacharTodasModal = () => {
        setPinSeguridad("");
        setShowDespacharTodasModal(false);
    }

    const reprogramarVisita = async () => {
        setReprogramandoVisita(true);

        if (await validarPINSeguridad(pinSeguridad)) {
            const response = await postRequest(`facturas/${facturaReprogramar.id}/reprogramar-visita`);
            
            if (response.success) {
                setPinSeguridad("");

                toast(`La factura codigo ${facturaReprogramar.numero_comprobante} ya puede ser despachada nuevamente`, "success");

                closeReprogramar();

                await Promise.all([
                 getFacturas(`facturas-despacho${filtersQuery}`),
                 getSummary(`facturas-despacho-summary${filtersQuery}`)
                ]);

            } else {
                toast(response.data.message, "error");
            }
        } else {
            toast("PIN incorrecto, intente de nuevo", "error");
        }

        setReprogramandoVisita(false);
    }

    const despacharTodas = async () => {
        setDespachandoTodas(true);
    
        if (await validarPINSeguridad(pinSeguridad)) {
            const { desde, hasta } = filters;

            const response = await postRequest(`despachos/todas?${desde ? `desde=${desde}` : ''}${desde && hasta ? '&' : ''}${hasta ? `hasta=${hasta}` : ''}`);
            
            if (response.success) {
                setPinSeguridad("");

                toast("Todas las facturas despachadas", "success");

                closeDespacharTodasModal();

                await Promise.all([
                 getFacturas(`facturas-despacho${filtersQuery}`),
                 getSummary(`facturas-despacho-summary${filtersQuery}`)
                ]);
            } else {
                toast(response.data.message, "error");
            }
        } else {
            toast("PIN incorrecto, intente de nuevo", "error");
        }

        setDespachandoTodas(false);
    };

    useEffect(() => {
        const url = showDespacho ? 'facturas-despacho' : 'despachos';
        getFacturas(`${url}${filtersQuery}`);
        getSummary(`facturas-despacho-summary${filtersQuery}`);
    }, [filtersQuery]);

    useEffect(() => {
        setSelected({ type: "reset" });
        setFacturas({ data: [] });
        setDespachos({ data: [] });

        const filters = {
            desde: showDespacho ? "" : dayjs().subtract(30, "day").startOf("day").format("YYYY-MM-DD"),
            hasta: dayjs().endOf("day").format("YYYY-MM-DD"),
            despacho: showDespacho ? "1" : null,
            buscar: "",
        }
        setFilters(filters);
        setUseFilters(filters);
    }, [showDespacho]);

    useEffect(() => setPinSeguridad(""), showDespacharTodasModal);
    
    useEffect(() => {
        getOptions();
    }, []);

    return <>
        <PageHeader hasButton={false}>
            <div className="btn-group d-none d-lg-flex w-100 mt-2" role="group">
                <div className="col-12 col-md d-flex align-items-center">
                    <div className="mr-3 d-flex flex-column justify-content-center" style={{gap: '0.05rem'}}>
                        <h2 className="m-0 p-0" style={{border: 'none', color: '#444', fontWeight: 500}}>Despacho de Facturas</h2>
                        <small className="text-muted">Resumen y detalle de despacho de facturas</small>
                    </div>

                    <button
                        type="button"
                        className={`btn btn-outline-info py-2 ${
                            showDespacho ? "active" : ""
                        }`}
                        style={{borderRadius: '6px'}}
                        onClick={() => {
                            setShowDespacho(true);
                        }}
                    >
                        <i className="fas fa-paper-plane mx-2"></i>Despachar
                    </button>

                    <button
                        type="button"
                        className={`btn btn-outline-info py-2 mx-2 ${
                            showDespacho ? "" : "active"
                        }`}
                        style={{borderRadius: '6px'}}
                        onClick={() => {
                        setShowDespacho(false);
                        }}
                    >
                        <i className="fas fa-history mx-2"></i>Historial
                    </button>
                    {showDespacho && !!facturas.data.length && <button
                        className="btn btn-outline-info py-2 tooltip-self"
                        onClick={() => openDespacharTodasModal()}
                    >
                        <i className="fas fa-file-signature"></i>
                        <span className="tootip-text tooltip-right">Despachar Todas</span>
                    </button>}
                </div>
                
                <div className="col-12 col-md-4 row pretty-form">                   
                    <div className="col-12 col-md form-group">
                        <label
                            htmlFor="desde"
                            style={{ fontSize: "12px", textTransform: "uppercase" }}
                        >
                            DESDE
                        </label>
                        <input
                            type="date"
                            name="desde"
                            id="desde"
                            className="form-control"
                            value={filters.desde}
                            onChange={(e) =>
                                onFilterChange("desde", e.target.value)
                            }
                        />
                    </div>
                    <div className="col-12 col-md form-group">
                        <label
                            htmlFor="hasta"
                            style={{ fontSize: "12px", textTransform: "uppercase" }}
                        >
                            HASTA
                        </label>
                        <input
                            type="date"
                            name="hasta"
                            id="hasta"
                            className="form-control"
                            value={filters.hasta}
                            onChange={(e) =>
                                onFilterChange("hasta", e.target.value)
                            }
                        />
                    </div>
                </div>
            </div>
        </PageHeader>

        {showDespacho && <DespachoSummary summary={summary} loading={isSummaring} />}

        <hr className="mb-4"/>
        
        <FiltersContainer className="pretty-form">
            <div className="col form-group">
                <label className="orden-filter-label">BUSCAR</label>
                <input
                    value={filters.buscar}
                    onChange={(e) => onFilterChange("buscar", e.target.value)}
                    type="search"
                    placeholder="Buscar NCF/Cliente..."
                    className="form-control"
                    name="buscar"
                />
            </div>

            <div className="col-12 col-md-6 col-lg-2 form-group">
                <label className="orden-filter-label">ZONA</label>
                <Select
                    id="zona"
                    options={zonas}
                    name="zona"
                    placeholder="Todas"
                    value={zonas.find((zona) => zona.value == filters.zona) ?? null}
                    onChange={(e) => onFilterChange("zona", e.value)}
                />
            </div>

            {showDespacho && (<>
                <div className="col-12 col-md-6 col-lg-2 form-group">
                    <label className="orden-filter-label">VENDEDOR</label>
                    <Select
                        id="vendedor"
                        options={vendedores}
                        name="vendedor"
                        placeholder="Todos"
                        value={vendedores.find((vendedor) => vendedor.value === filters.vendedor) ?? null}
                        onChange={(e) => onFilterChange("vendedor", e.value)}
                    />
                </div>

                <div className="col-12 col-md-6 col-lg-2 form-group">
                    <label className="orden-filter-label">SEGUIMIENTO</label>
                    <Select
                        id="seguimiento"
                        options={seguimientos}
                        name="seguimiento"
                        placeholder="Todos"
                        value={seguimientos.find((seguimiento) => seguimiento.value === filters.seguimiento) ?? null}
                        onChange={(e) => onFilterChange("seguimiento", e.value)}
                    />
                </div>

                <div className="col-12 col-md-6 col-lg-2 form-group">
                    <label className="orden-filter-label">TIPO DE ENTREGA</label>
                    <Select
                        id="tipo_despacho"
                        options={tipos}
                        name="tipo_despacho"
                        placeholder="Todas"
                        value={tipos.find((tipo) => tipo.value === filters.tipo_despacho) ?? null}
                        onChange={(e) => onFilterChange("tipo_despacho", e.value)}
                    />
                </div>
            </>)}

            {!showDespacho && (<>
                <div className="col-12 col-md-6 col-lg-2 form-group">
                    <label>Empleado</label>
                    <Select
                        id="empleado"
                        options={empleados}
                        name="empleado"
                        placeholder="Todos"
                        value={empleados.find((empleado) => empleado.value === filters.empleado) ?? null}
                        onChange={(e) => onFilterChange("empleado", e.value)}
                    />
                </div>

                <div className="col-12 col-md-6 col-lg-2 form-group">
                    <label className="orden-filter-label">Vehículo</label>
                    <Select
                        id="vehiculo"
                        options={vehiculos}
                        name="vehiculo"
                        value={vehiculos.find((vehiculo) => vehiculo.value === filters.vehiculo) ?? null}
                        onChange={(e) => onFilterChange("vehiculo", e.value)}
                    />
                </div>

                <div className="col-12 col-md-6 col-lg-2 form-group">
                    <label className="orden-filter-label">Tipo de entrega</label>
                    <Select
                        id="tipo_despacho"
                        options={tipos}
                        name="tipo_despacho"
                        placeholder="Todas"
                        value={tipos.find((tipo) => tipo.value === filters.tipo_despacho) ?? null}
                        onChange={(e) => onFilterChange("tipo_despacho", e.value)}
                    />
                </div>

                <div className="col-12 col-md-6 col-lg-2 form-group">
                    <label className="orden-filter-label">Estado</label>
                    <Select
                        id="estado"
                        options={estados}
                        name="estado"
                        placeholder="Todas"
                        value={estados.find((e) => e.value === filters.estado) ?? null}
                        onChange={(e) => onFilterChange("estado", e.value)}
                    />
                </div>
            </>)}
        </FiltersContainer>

        <div
			className="table-data table-responsive table-row__desktop"
			style={{ overflowX: "visible" }}
		>
            {isLoading && <Loader />}

			{!isLoading && (<table className="table table-data2">
				{showDespacho && <>
                    <thead>
                        <tr>
                            <th>
                                {selected.tipo_despacho && <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={selected.allSelected}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setSelected({ type: "all", payload: { facturas: facturas.data } });
                                        } else {
                                            setSelected({ type: "reset" });
                                        }
                                    }} />     
                                }
                                NCF/Numero
                            </th>
                            <th>Cliente</th>
                            <th>Fecha</th>
                            <th>Total</th>
                            <th>Vendedor</th>
                            <th>Zona</th>
                            <th>Seguimiento</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {facturas.data.map((factura) => {
                            const isSelected = (selected.allSelected && factura.tipo_despacho === selected.tipo_despacho) || selected.facturas.some((f) => f.id === factura.id);

                            return <tr className="tr-shadow" key={factura.id} style={{display: selected.tipo_despacho && selected.tipo_despacho !== factura.tipo_despacho ? 'none' : 'table-row'}}>
                                <td>
                                    <div className="form-check d-flex flex-column">
                                        {!factura.despachada && <input
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => handleSelect(factura)}
                                        />}
                                        <strong
                                            className="form-check-label"
                                            htmlFor="defaultCheck1"
                                        >
                                            {factura.numero_comprobante}
                                        </strong>
                                        <span class="badge badge-info badge-light py-2 px-1 text-capitalize mr-auto">Factura</span>
                                    </div>
                                </td>
                                <td style={{width: '250px'}}>
                                    <Link
                                        to={getLink(`/contactos/${factura.cliente?.id}/transacciones`)}
                                        className="ml-2 contacto"
                                        onClick={() => {localStorage.setItem("contacto", JSON.stringify(factura.cliente));}}
                                    >
                                            {factura.tipo_despacho === "Carga" && <svg className="mr-1" style={{height: "16px", width: "16px", fill: "#007bff"}} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><path d="M64 160C64 124.7 92.7 96 128 96L416 96C451.3 96 480 124.7 480 160L480 192L530.7 192C547.7 192 564 198.7 576 210.7L621.3 256C633.3 268 640 284.3 640 301.3L640 448C640 483.3 611.3 512 576 512L572.7 512C562.3 548.9 528.3 576 488 576C447.7 576 413.8 548.9 403.3 512L300.7 512C290.3 548.9 256.3 576 216 576C175.7 576 141.8 548.9 131.3 512L128 512C92.7 512 64 483.3 64 448L64 400L24 400C10.7 400 0 389.3 0 376C0 362.7 10.7 352 24 352L136 352C149.3 352 160 341.3 160 328C160 314.7 149.3 304 136 304L24 304C10.7 304 0 293.3 0 280C0 266.7 10.7 256 24 256L200 256C213.3 256 224 245.3 224 232C224 218.7 213.3 208 200 208L24 208C10.7 208 0 197.3 0 184C0 170.7 10.7 160 24 160L64 160zM576 352L576 301.3L530.7 256L480 256L480 352L576 352zM256 488C256 465.9 238.1 448 216 448C193.9 448 176 465.9 176 488C176 510.1 193.9 528 216 528C238.1 528 256 510.1 256 488zM488 528C510.1 528 528 510.1 528 488C528 465.9 510.1 448 488 448C465.9 448 448 465.9 448 488C448 510.1 465.9 528 488 528z"/></svg>}
                                            {factura.tipo_despacho !== "Carga" && <svg className="mr-1" style={{height: "16px", width: "16px", fill: "#007bff"}} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><path d="M32 206.1L32 544C32 561.7 46.3 576 64 576C81.7 576 96 561.7 96 544L96 304C96 286.3 110.3 272 128 272L512 272C529.7 272 544 286.3 544 304L544 544C544 561.7 558.3 576 576 576C593.7 576 608 561.7 608 544L608 206.1C608 178.6 590.4 154.1 564.2 145.4L335.2 69.1C325.3 65.8 314.7 65.8 304.8 69.1L75.8 145.4C49.6 154.1 32 178.6 32 206.1zM496 320L144 320L144 384L496 384L496 320zM144 480L496 480L496 416L144 416L144 480zM496 512L144 512L144 576L496 576L496 512z"/></svg>}
                                        {factura.cliente?.codigo} - {factura.cliente?.nombre}
                                    </Link>
                                    {/* <div className="ml-2 h-100 w-100 d-flex items-center">
                                        <span><strong>Seguimiento:</strong> {factura.seguimiento}</span>
                                    </div>
                                    {factura.planificador_tarea_id && <PlanificadorTareaEstatus id={factura.planificador_tarea_id}></PlanificadorTareaEstatus>} */}
                                </td>
                                <td>{dayjs(factura.fecha).format("DD/MM/YYYY" )}</td>
                                <td style={{fontWeight: 600}}>{formatCurrency(factura.total, "$")}</td>
                                <td style={{width: '250px'}}>
                                    {factura.vendedor
                                        ? <AvatarEmpleado empleado={factura.vendedor} />
                                        : ''}
                                </td>
                                <td>
                                    {factura.ruta && <>
                                        {factura.ruta?.nombre}
                                    </>}
                                </td>
                                <td style={{width: '100px'}}>
                                    {factura.seguimiento && 
                                        <span className={[
                                            "badge badge-light p-2 text-capitalize",
                                            factura.seguimiento === "Por Cargar" ? "badge-danger" : "",
                                            factura.seguimiento === "Por Entregar" ? "badge-info" : "",
                                            factura.seguimiento === "Entregada" ? "badge-success" : "",
                                            factura.seguimiento === "Cargada" ? "badge-success" : "",
                                        ].join(" ")}>{factura.seguimiento}</span>}
                                </td>
                                <td style={{width: '100px'}}>
                                    <Dropdown
                                        btnText=""
                                        btnIcon={<i className="zmdi zmdi-more-vert"></i>}
                                        styles={{
                                            dropdownMenu: {
                                                transform: "translateX(calc(40px - 100%))",
                                            },
                                        }}>
                                        <Link
                                            to={getLink(
                                                `/imprimir/facturas/${factura.id}`
                                            )}
                                            target="_blank"
                                            className="dropdown-item"
                                            title="Impresión normal"
                                        >
                                            Imprimir factura
                                        </Link>
                                        {factura.despacho_id && <>
                                            <Link
                                                to={getLink(
                                                    `/imprimir/ingresos/despacho-facturas/reporte?id=${factura.despacho_id}`
                                                )}
                                                target="_blank"
                                                className="dropdown-item"
                                                title="Impresión normal"
                                            >
                                                Imprimir despacho
                                            </Link>

                                            <button className="dropdown-item" onClick={() => openReprogramar(factura)}>
                                                Reprogramar Visita
                                            </button>
                                        </>}
                                    </Dropdown>
                                </td>
                            </tr>;
                        })}
                    </tbody>
                </>}

                {!showDespacho && <>
                    <thead>
                        <tr>
                            <th>Fecha de Entrega</th>
                            <th>No. Entrega</th>
                            <th>Zona</th>
                            <th>Empleado</th>
                            <th>Vehículo</th>
                            <th>Cantidad de Facturas</th>
                            <th>Total</th>
                            <th>Estado</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>

                    <tbody>
                        {despachos.data.map((despacho) => (
                            <tr className="tr-shadow" key={despacho.id}>
                                <td>{dayjs(despacho.fecha_entrega).format("DD/MM/YYYY hh:mm")}</td>
                                <td>
                                    {despacho.tipo_despacho === "Carga" && <svg className="mr-1" style={{height: "16px", width: "16px", fill: "#007bff"}} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><path d="M64 160C64 124.7 92.7 96 128 96L416 96C451.3 96 480 124.7 480 160L480 192L530.7 192C547.7 192 564 198.7 576 210.7L621.3 256C633.3 268 640 284.3 640 301.3L640 448C640 483.3 611.3 512 576 512L572.7 512C562.3 548.9 528.3 576 488 576C447.7 576 413.8 548.9 403.3 512L300.7 512C290.3 548.9 256.3 576 216 576C175.7 576 141.8 548.9 131.3 512L128 512C92.7 512 64 483.3 64 448L64 400L24 400C10.7 400 0 389.3 0 376C0 362.7 10.7 352 24 352L136 352C149.3 352 160 341.3 160 328C160 314.7 149.3 304 136 304L24 304C10.7 304 0 293.3 0 280C0 266.7 10.7 256 24 256L200 256C213.3 256 224 245.3 224 232C224 218.7 213.3 208 200 208L24 208C10.7 208 0 197.3 0 184C0 170.7 10.7 160 24 160L64 160zM576 352L576 301.3L530.7 256L480 256L480 352L576 352zM256 488C256 465.9 238.1 448 216 448C193.9 448 176 465.9 176 488C176 510.1 193.9 528 216 528C238.1 528 256 510.1 256 488zM488 528C510.1 528 528 510.1 528 488C528 465.9 510.1 448 488 448C465.9 448 448 465.9 448 488C448 510.1 465.9 528 488 528z"/></svg>}
                                    {despacho.tipo_despacho !== "Carga" && <svg className="mr-1" style={{height: "16px", width: "16px", fill: "#007bff"}} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><path d="M32 206.1L32 544C32 561.7 46.3 576 64 576C81.7 576 96 561.7 96 544L96 304C96 286.3 110.3 272 128 272L512 272C529.7 272 544 286.3 544 304L544 544C544 561.7 558.3 576 576 576C593.7 576 608 561.7 608 544L608 206.1C608 178.6 590.4 154.1 564.2 145.4L335.2 69.1C325.3 65.8 314.7 65.8 304.8 69.1L75.8 145.4C49.6 154.1 32 178.6 32 206.1zM496 320L144 320L144 384L496 384L496 320zM144 480L496 480L496 416L144 416L144 480zM496 512L144 512L144 576L496 576L496 512z"/></svg>}

                                    {despacho.numero_despacho}</td>
                                <td>{despacho.zona?.nombre ?? 'N/A'}</td>
                                <td>{despacho.empleado ? `${despacho.empleado?.nombre} ${despacho.empleado?.apellido}` : 'N/A'}</td>
                                <td>{despacho.vehiculo?.ficha ?? 'N/A'}</td>
                                <td>{despacho.cantidad_facturas}</td>
                                <td>{formatCurrency(despacho.total, "$")}</td>
                                <td className="text-capitalize"><span className={`badge badge-light badge-${despacho.estado === 'cerrado' ? 'danger' : 'success'}`}>{despacho.estado}</span></td>
                                <td>
                                    <Dropdown 
                                        btnText=""
                                        btnIcon={<i className="zmdi zmdi-more-vert m-auto" />}
                                        classes={{ dropdownMenu: "align-right" }}
                                    >
                                        {auth.can(permisions.reporteDespachoFacturasListaCarga) && <Link
                                            to={getLink(
                                                `/imprimir/ingresos/despacho-facturas/lista-carga?id=${despacho.id}`
                                            )}
                                            target="_blank"
                                            className="dropdown-item"
                                            title="Impresión normal"
                                        >
                                            Lista de carga
                                        </Link>}

                                        {auth.can(permisions.reporteDespachoFacturasOrdenCarga) && <Link
                                            to={getLink(
                                                `/imprimir/ingresos/despacho-facturas/orden-carga?id=${despacho.id}`
                                            )}
                                            target="_blank"
                                            className="dropdown-item"
                                            title="Impresión normal"
                                        >
                                            Orden de carga
                                        </Link>}

                                        {despacho.estado !== 'cerrado' && <button className="dropdown-item" onClick={() => openCloseModal(despacho)}>Cerrar</button>}
                                        {despacho.estado === 'cerrado' && <button className="dropdown-item" onClick={() => openReOpenModal(despacho)}>Reabrir</button>}

                                        <Link
                                            to={getLink(
                                                `/imprimir/ingresos/despacho-facturas/reporte?id=${despacho.id}`
                                            )}
                                            target="_blank"
                                            className="dropdown-item"
                                            title="Impresión normal"
                                        >
                                            Despacho de facturas
                                        </Link>
                                    </Dropdown>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </>}
			</table>)}
		</div>

        {showDespacho && facturas.links && (
            <Pagination
                links={facturas.links}
                meta={facturas.meta}
                getData={getFacturas}
            />
        )}

        {!showDespacho && despachos.links && (
            <Pagination
                links={despachos.links}
                meta={despachos.meta}
                getData={getFacturas}
            />
        )}

        <Modal
            show={showGenerarDespachoReporteModal}
            blank={true}
            size={"600px"}
            toggle={() => {
                setShowGenerarDespachoReporteModal(false);
            }}
        >
            <GenerarReporteDespachoForm
                facturas={selected.facturas}
                tipo_despacho={selected.tipo_despacho}
                zonas={zonas}
                empleados={empleados}
                vehiculos={vehiculos}
                all={selected.allSelected}
                url={`facturas-despacho${filtersQuery}`}
                onSuccess={() => {
                    setSelected({ type: "reset" });
                    Promise.all([
                        getFacturas(`facturas-despacho${filtersQuery}`),
                        getSummary(`facturas-despacho-summary${filtersQuery}`)
                    ]);
                    setShowGenerarDespachoReporteModal(false);
                    setShowDespacho(false);
                }}
                onCancel={() => {
                    setShowGenerarDespachoReporteModal(false);
                }}
            />
        </Modal>

        <Modal
            title="Despachar todas las facturas"
            show={showDespacharTodasModal}
            blank={false}
            callback={despacharTodas}
            toggle={closeDespacharTodasModal}>
                {
                    despachandoTodas 
                    ? <><Loader /></> 
                    : <>
                        <p className="px-3 pb-3">¿Estás seguro que desea despachar todas las facturas? (Esta acción no se puede deshacer).</p>
                
                        <div className="form-group">
                            <label>PIN de seguridad</label>
                            <input
                                type="password"
                                className="form-control"
                                id="PIN"
                                name="pin_seguridad"
                                value={pinSeguridad}
                                onChange={(e) =>
                                    setPinSeguridad(e.target.value)
                                }
                            />
                        </div>
                    </>
                }
        </Modal>

        <Modal
            title="Reprogramar visita"
            show={showReprogramarModal}
            blank={false}
            callback={reprogramarVisita}
            toggle={closeReprogramar}>
                {
                    reprogramandoVisita 
                    ? <><Loader /></> 
                    : <>
                        <p className="px-3 pb-3">¿Estás seguro que desea reprogramar la visita?.</p>
                
                        <div className="form-group">
                            <label>PIN de seguridad</label>
                            <input
                                type="password"
                                className="form-control"
                                id="PIN"
                                name="pin_seguridad"
                                value={pinSeguridad}
                                onChange={(e) =>
                                    setPinSeguridad(e.target.value)
                                }
                            />
                        </div>
                    </>
                }
        </Modal>

        <Modal
            show={showCloseModal}
            blank={false}
            size={"600px"}
            acceptText="Cerrar Despacho"
            toggle={closeCloseModal}
            callback={closeDespacho}
            saving={ejecutando}
        >
            <p>
                ¿Esta seguro que desea cerrar el despacho de facturas No. <strong>`{despacho?.numero_despacho}`</strong>?
            </p>

            <div className="row mt-2">
                <small className="text-danger mx-auto">Al cerrar se afectara el seguimiento de las <strong>{despacho?.cantidad_facturas}</strong> facturas presente.</small>
            </div>
        </Modal>

        <Modal
            show={showReopenModal}
            blank={false}
            toggle={closeReOpenModal}
            acceptText="Reabrir Despacho"
            callback={reabrirDespacho}
            saving={ejecutando}
        >
        <p>
            ¿Esta seguro que desea reabrir el despacho de facturas No. <strong>`{despacho?.numero_despacho}`</strong>?
        </p>

        <div className="row mt-2 mb-3">
            <small className="text-danger mx-auto">Al reabrir se afectara el seguimiento de las <strong>{despacho?.cantidad_facturas}</strong> facturas presente.</small>
        </div>

        <div className="form-group">
            <label>PIN de seguridad</label>
            <input
                type="password"
                className="form-control"
                id="PIN"
                name="pin_seguridad"
                value={pinSeguridad}
                onChange={(e) => setPinSeguridad(e.target.value)}
            />
            <p
                className="font-weight-bold"
                style={{
                    color: "gray",
                    fontStyle: "italic",
                    lineHeight: "13px",
                    fontSize: "0.7rem",
                }}
            >
                Para continuar ingrese PIN de seguridad
            </p>
        </div>
    </Modal>

        {selected.facturas.length > 0 && (
            <button
                className="btn btn-info"
                onClick={() => setShowGenerarDespachoReporteModal(true)}
                style={{ position: "fixed", bottom: "20px", left: "50%", transform: "translateX(-50%)", zIndex: 1000 }}
            >
                + DESPACHAR
            </button>
        )}
    </>;
}

export default DespachoFacturas;
